import { useEffect, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  CircleMarker,
} from 'react-leaflet';
import L from 'leaflet';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { useApp } from '@/store';
import { Status } from '@/data/types';
import { useT } from '@/lib/i18n';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Fix default icon
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

const statusColor: Record<Status, string> = {
  [Status.DONE]: '#10b981',
  [Status.WAITING]: '#f59e0b',
  [Status.TODO]: '#3b82f6',
  [Status.NONE]: '#94a3b8',
};

export function MapPage() {
  const t = useT();
  const customers = useApp(s => s.customers);
  const [layer, setLayer] = useState('osm');
  const [show, setShow] = useState({
    done: true,
    waiting: true,
    todo: true,
    none: true,
    labels: false,
  });
  const [center] = useState<[number, number]>([47.05, 8.3]);

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, []);

  const visible = customers.filter(c => show[c.status] && c.lat && c.lng);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold tracking-tight">{t('map.title')}</h1>
        <Card className="flex items-center flex-wrap gap-2 sm:gap-4 p-3">
          <Select value={layer} onValueChange={setLayer}>
            <SelectTrigger className="w-full sm:w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* i18n-ignore: "OpenStreetMap","Swisstopo","Google" */}
              <SelectItem value="osm">{'OpenStreetMap'}</SelectItem>
              <SelectItem value="swisstopo">{'Swisstopo'}</SelectItem>
              <SelectItem value="google">{'Google'}</SelectItem>
            </SelectContent>
          </Select>
          {[Status.DONE, Status.WAITING, Status.TODO, Status.NONE].map(k => (
            <div key={k} className="flex items-center gap-2">
              <Checkbox
                id={`m-${k}`}
                checked={show[k]}
                onCheckedChange={v => setShow({ ...show, [k]: !!v })}
              />
              <Label htmlFor={`m-${k}`} className="text-xs capitalize">
                {k}
              </Label>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Checkbox
              id="labels"
              checked={show.labels}
              onCheckedChange={v => setShow({ ...show, labels: !!v })}
            />
            <Label htmlFor="labels" className="text-xs">
              Labels
            </Label>
          </div>
        </Card>
      </div>

      <Card
        className="overflow-hidden p-0"
        style={{ height: 'calc(100dvh - 200px)', minHeight: '300px' }}
        data-tour="map-view"
      >
        <MapContainer
          center={center}
          zoom={8}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution={
              layer === 'swisstopo'
                ? '© swisstopo'
                : layer === 'google'
                  ? '© Google'
                  : '© OpenStreetMap'
            }
            url={
              layer === 'swisstopo'
                ? 'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-farbe/default/current/3857/{z}/{x}/{y}.jpeg'
                : layer === 'google'
                  ? 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}'
                  : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            }
          />
          {visible.map(c => (
            <CircleMarker
              key={c.id}
              center={[c.lat!, c.lng!]}
              radius={10}
              pathOptions={{
                color: statusColor[c.status],
                fillColor: statusColor[c.status],
                fillOpacity: 0.8,
              }}
            >
              <Popup>
                <div className="space-y-1">
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs">
                    {c.objectAddress?.street}, {c.objectAddress?.city}
                  </div>
                  <Link
                    to={`/customer/${c.id}`}
                    className="text-primary text-xs"
                  >
                    Open →
                  </Link>
                </div>
              </Popup>
            </CircleMarker>
          ))}
          {visible
            .filter(() => show.labels)
            .map(c => (
              <Marker key={`l-${c.id}`} position={[c.lat!, c.lng!]}>
                <Popup>{c.name}</Popup>
              </Marker>
            ))}
        </MapContainer>
      </Card>
    </motion.div>
  );
}
